import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Fade,
  LinearProgress,
  Paper,
  Skeleton
} from '@mui/material';
import { keyframes } from '@mui/system';

// Animación de pulso para el logo/icono
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Animación de aparición gradual para el texto
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

function LoadingInformation({
  message = "Cargando información...",
  subtitle = "Por favor espere mientras procesamos los datos",
  showProgress = true,
  variant = "default" // "default", "minimal", "detailed"
}) {
  const [dots, setDots] = useState('');
  const [progress, setProgress] = useState(0);

  // Animación de puntos para el texto
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Simulación de progreso (puedes conectar esto con el progreso real de la API)
  useEffect(() => {
    if (showProgress) {
      const timer = setInterval(() => {
        setProgress(prevProgress => {
          if (prevProgress >= 100) {
            return 0;
          }
          const diff = Math.random() * 10;
          return Math.min(prevProgress + diff, 95); // No llegues al 100% para mantener la sensación de carga
        });
      }, 800);
      return () => clearInterval(timer);
    }
  }, [showProgress]);

  if (variant === "minimal") {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3
      }}>
        <CircularProgress
          size={32}
          thickness={4}
          sx={{ color: '#f759ab' }}
        />
        <Typography variant="body2" sx={{ ml: 2, color: '#666' }}>
          {message}
        </Typography>
      </Box>
    );
  }

  if (variant === "detailed") {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Efectos de fondo */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          animation: `${pulse} 3s ease-in-out infinite`,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          animation: `${pulse} 4s ease-in-out infinite`,
        }} />

        <Paper elevation={10} sx={{
          p: 6,
          borderRadius: 4,
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          maxWidth: 400,
          width: '90%'
        }}>
          {/* Logo o icono principal */}
          <Box sx={{
            mb: 3,
            animation: `${pulse} 2s ease-in-out infinite`,
          }}>
            <CircularProgress
              size={80}
              thickness={3}
              sx={{
                color: '#f759ab',
                filter: 'drop-shadow(0 4px 8px rgba(247, 89, 171, 0.3))'
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#333',
              animation: `${fadeInUp} 0.6s ease-out`
            }}
          >
            {message}{dots}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: '#666',
              animation: `${fadeInUp} 0.6s ease-out 0.2s both`
            }}
          >
            {subtitle}
          </Typography>

          {showProgress && (
            <Box sx={{
              width: '100%',
              mb: 2,
              animation: `${fadeInUp} 0.6s ease-out 0.4s both`
            }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(247, 89, 171, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #f759ab 0%, #ff6b9d 100%)',
                  }
                }}
              />
              <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                {Math.round(progress)}% completado
              </Typography>
            </Box>
          )}

          {/* Skeleton de contenido */}
          <Box sx={{ mt: 3 }}>
            <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
          </Box>
        </Paper>
      </Box>
    );
  }

  // Variante por defecto mejorada
  return (
    <Fade in={true} timeout={500}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fc 0%, #e9ecef 100%)',
        position: 'relative'
      }}>
        {/* Contenedor principal */}
        <Paper elevation={3} sx={{
          p: 5,
          borderRadius: 3,
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(247, 89, 171, 0.1)'
        }}>
          {/* Spinner principal */}
          <Box sx={{
            mb: 3,
            position: 'relative',
            display: 'inline-block'
          }}>
            <CircularProgress
              size={70}
              thickness={4}
              sx={{
                color: '#f759ab',
                filter: 'drop-shadow(0 2px 4px rgba(247, 89, 171, 0.2))',
              }}
            />
          </Box>

          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 500,
              color: '#333',
              fontSize: '1.25rem'
            }}
          >
            {message}{dots}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mb: showProgress ? 3 : 0,
              maxWidth: 300
            }}
          >
            {subtitle}
          </Typography>
        </Paper>

        {/* Indicador adicional en la parte inferior */}
        <Typography
          variant="caption"
          sx={{
            mt: 3,
            color: '#999',
            animation: `${fadeInUp} 0.6s ease-out 0.8s both`
          }}
        >
          D'princess Foto Films - Sistema de Gestión
        </Typography>
      </Box>
    </Fade>
  );
}

export default LoadingInformation;